import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Card, CardContent } from '../components/ui/card';
import { Megaphone, Plus, Pencil, Trash2, GripVertical, CalendarRange } from 'lucide-react';
import { confirmDialog } from '../components/ConfirmHost';
import { AnnouncementCard, ANN_TYPES } from '../components/LoginAnnouncements';
import { AnnouncementFormDialog } from '../components/settings/AnnouncementFormDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const errText = (e) => (typeof e.response?.data?.detail === 'string' ? e.response.data.detail : e.response?.data?.detail?.[0]?.msg?.replace('Value error, ', '') || 'İşlem başarısız');
const fmt = (d) => (d ? `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}` : null);
const strip = ({ id, order, created_at, created_by, updated_at, ...rest }) => rest;

const today = () => new Date().toISOString().slice(0, 10);
const liveState = (a) => {
  if (!a.is_active) return ['Pasif', 'bg-slate-100 text-slate-500'];
  if (a.start_date && a.start_date > today()) return ['Planlandı', 'bg-sky-100 text-sky-700'];
  if (a.end_date && a.end_date < today()) return ['Süresi doldu', 'bg-slate-100 text-slate-500'];
  return ['Yayında', 'bg-emerald-100 text-emerald-700'];
};

const Row = ({ a, onEdit, onDelete, onToggle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: a.id });
  const [state, cls] = liveState(a);
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} className={`relative ${isDragging ? 'z-10 opacity-80' : ''}`} data-testid={`ann-row-${a.id}`}>
      <Card className="border-slate-200 hover:shadow-md transition-shadow">
        <CardContent className="p-3 flex items-start gap-3">
          <button type="button" className="mt-1 cursor-grab text-slate-400 hover:text-slate-700 active:cursor-grabbing" {...attributes} {...listeners} data-testid={`ann-drag-${a.id}`} aria-label="Sürükle"><GripVertical className="h-5 w-5" /></button>
          <div className="flex-1 min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline">{ANN_TYPES[a.type]?.label}</Badge>
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`} data-testid={`ann-state-${a.id}`}>{state}</span>
              <span className="flex items-center gap-1 text-xs text-slate-500"><CalendarRange className="h-3.5 w-3.5" />{fmt(a.start_date) || 'Hemen'} – {fmt(a.end_date) || 'Süresiz'}</span>
            </div>
            <AnnouncementCard a={a} />
          </div>
          <div className="flex items-center gap-1">
            <Switch checked={a.is_active} onCheckedChange={(v) => onToggle(a, v)} data-testid={`ann-toggle-${a.id}`} />
            <Button size="icon" variant="ghost" className="text-blue-600" onClick={() => onEdit(a)} data-testid={`ann-edit-${a.id}`}><Pencil className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => onDelete(a)} data-testid={`ann-delete-${a.id}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default function LoginAnnouncementsPage() {
  const { user, canView, canTab } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const load = () => axios.get(`${API}/login-announcements`).then(r => setItems(r.data)).catch(() => toast.error('Duyurular yüklenemedi'));
  useEffect(() => { if (canView('login_announcements')) load(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (data) => {
    try {
      if (editing?.id) await axios.put(`${API}/login-announcements/${editing.id}`, strip(data));
      else await axios.post(`${API}/login-announcements`, strip(data));
      toast.success('Duyuru kaydedildi');
      setEditing(null);
      load();
    } catch (e) { toast.error(errText(e)); }
  };
  const toggle = async (a, v) => { try { await axios.put(`${API}/login-announcements/${a.id}`, strip({ ...a, is_active: v })); load(); } catch (e) { toast.error(errText(e)); } };
  const remove = async (a) => {
    if (!(await confirmDialog({ title: 'Duyuru silinsin mi?', description: a.title || 'Bu duyuru giriş sayfasından kaldırılacak.', confirmLabel: 'Evet, Sil' }))) return;
    try { await axios.delete(`${API}/login-announcements/${a.id}`); toast.success('Duyuru silindi'); load(); } catch (e) { toast.error(errText(e)); }
  };
  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const next = arrayMove(items, items.findIndex(i => i.id === active.id), items.findIndex(i => i.id === over.id));
    setItems(next);
    try { await axios.put(`${API}/login-announcements/reorder`, { ids: next.map(i => i.id) }); toast.success('Sıralama kaydedildi'); } catch (e) { toast.error(errText(e)); load(); }
  };

  if (!canView('login_announcements')) return <p className="text-slate-500">Bu sayfa sadece Süper Admin kullanıcılar içindir.</p>;
  return (
    <div className="space-y-6 max-w-4xl" data-testid="login-announcements-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Megaphone className="h-6 w-6 text-orange-500" />Giriş Sayfası Duyuruları</h1>
          <p className="text-slate-500 mt-1 max-w-2xl">Buraya yazdığınız duyurular giriş sayfasında, kullanıcı adı ve şifre kutusunun altında herkese görünür. Sırayı sol taraftaki tutamaçla sürükleyerek değiştirin.</p>
        </div>
        <Button onClick={() => setEditing({})} className="bg-slate-900 hover:bg-slate-800" data-testid="ann-add-btn"><Plus className="h-4 w-4 mr-1" />Yeni Duyuru</Button>
      </div>
      {items.length === 0
        ? <Card className="border-dashed"><CardContent className="p-10 text-center text-slate-500" data-testid="ann-empty">Henüz duyuru yok.</CardContent></Card>
        : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-3" data-testid="ann-list">{items.map(a => <Row key={a.id} a={a} onEdit={setEditing} onDelete={remove} onToggle={toggle} />)}</div>
            </SortableContext>
          </DndContext>
        )}
      <AnnouncementFormDialog open={!!editing} item={editing} onClose={() => setEditing(null)} onSave={save} />
    </div>
  );
}
